import React, { useCallback, useEffect, useRef, useState } from 'react'

import useAuth from 'hooks/useAuth'

import mixpanel from 'helpers/mixpanel'
import * as service from 'service'

import DaiChatContext from 'contexts/DaiChatContext'

const DAI_DEFAULT_AGENT = 'dai_agent'

const DaiChatProvider = ({ children }) => {
  // --- Estado de controle (open/close, agente, contexto) ---
  const [chatOpen, setChatOpen] = useState(false)
  const [agentName, setAgentName] = useState(DAI_DEFAULT_AGENT)
  const [chatContext, setChatContext] = useState({})
  const [initialMessage, setInitialMessage] = useState(null)

  // --- Estado da instância do chat ---
  const [daiChat, setDaiChat] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [inputMessage, setInputMessage] = useState('')

  // --- Estado da modal de confirmação de novo chat ---
  const [newChatDialogOpen, setNewChatDialogOpen] = useState(false)
  const pendingNewChatParamsRef = useRef(null)

  // --- Refs ---
  const messagesContainerRef = useRef(null)
  const chatContainerRef = useRef(null)
  const abortControllerRef = useRef(null)
  const inputRef = useRef(null)
  const isSendingRef = useRef(false)
  const daiChatRef = useRef(null)

  const { user } = useAuth()

  const hasMessage = messages.length > 0

  const registerMixpanelEvent = useCallback((action) => {
    mixpanel.track('DAI', 'Chat Plataforma', {
      action: `dai-chat-plataforma-${action}`,
      page: window.location.pathname,
      pageTitle: document.title,
    })
  }, [])

  const setIsSendingSync = useCallback((val) => {
    isSendingRef.current = val
    setIsSending(val)
  }, [])

  const setDaiChatSync = useCallback((val) => {
    daiChatRef.current = val
    setDaiChat(val)
  }, [])

  // Foco ao abrir o chat
  useEffect(() => {
    if (!chatOpen) return

    const focusTimer = setTimeout(() => inputRef.current?.focus(), 100)
    // MUI v4's TextareaAutosize listens to window 'resize' to re-run syncHeight.
    // When the panel opens, syncHeight fires via useLayoutEffect while the CSS
    // transition is at width:0, which makes the placeholder text wrap and sets
    // the textarea height to maxRows. Dispatching resize after the transition
    // (~225ms) forces a correct recalculation at the panel's final width.
    const resizeTimer = setTimeout(
      () => window.dispatchEvent(new Event('resize')),
      300,
    )

    return () => {
      clearTimeout(focusTimer)
      clearTimeout(resizeTimer)
    }
  }, [chatOpen])

  // Aborta streaming ao desmontar
  useEffect(() => {
    return () => abortControllerRef.current?.abort()
  }, [])

  // --- Funções internas ---

  const resetChatState = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort()
    setDaiChatSync(null)
    setMessages([])
    setLoading(false)
    setIsSendingSync(false)
    setInputMessage('')
    setAgentName(DAI_DEFAULT_AGENT)
    setChatContext({})
  }

  const updateLastMessage = (updater) => {
    setMessages((prev) => {
      const msgs = [...prev]
      const lastIdx = msgs.length - 1
      if (lastIdx >= 0 && msgs[lastIdx].streaming) {
        return [...msgs.slice(0, lastIdx), updater(msgs[lastIdx])]
      }
      return msgs
    })
  }

  const findOrCreateSession = async (appName, context) => {
    if (daiChatRef.current?.sessionId) return daiChatRef.current

    const userId = String(user?.id)

    const response = await service.dai.chat.createSession({
      appName,
      userId,
      ...(Object.keys(context).length > 0 && { state: context }),
    })

    const sessionId = response.data?.id || response.data?.sessionId
    const sessionData = { sessionId, userId }

    setDaiChatSync(sessionData)

    return sessionData
  }

  const runAdkMessageSSE = async (session, userMessage, signal, appName) => {
    let accumulatedText = ''

    setMessages((prev) => [
      ...prev,
      {
        role: 'ai',
        content: '',
        streaming: true,
        phase: 'thinking',
        activeToolName: null,
        completedTools: [],
      },
    ])
    setLoading(false)

    await service.dai.chat.runSSE(
      {
        appName,
        sessionId: session.sessionId,
        newMessage: { role: 'user', parts: [{ text: userMessage }] },
        streaming: true,
      },
      {
        onMessage: (event) => {
          const parts = event?.content?.parts ?? []

          const textChunk = parts
            .filter((p) => p.text)
            .map((p) => p.text)
            .join('')

          const functionCallParts = parts.filter((part) => part.functionCall)
          const functionCallNames = functionCallParts.map(
            (part) => part.functionCall.name,
          )
          const functionResponseNames = parts
            .filter((part) => part.functionResponse)
            .map((part) => part.functionResponse.name)

          if (functionCallNames.length > 0) {
            const lastFunctionCall =
              functionCallParts[functionCallParts.length - 1].functionCall

            const resolveFunctionCallArgs = (fc) => {
              if (!fc.args) return {}
              if (typeof fc.args === 'string') {
                try {
                  return JSON.parse(fc.args)
                } catch {
                  return {}
                }
              }
              return fc.args
            }

            if (lastFunctionCall.name === 'transfer_to_agent') {
              const resolvedAgentName =
                resolveFunctionCallArgs(lastFunctionCall)?.agent_name ||
                'transfer_to_agent'

              updateLastMessage((message) => ({
                ...message,
                phase: 'tool_calling',
                activeToolName: resolvedAgentName,
                pendingTransferAgent: resolvedAgentName,
              }))
            } else {
              updateLastMessage((message) => ({
                ...message,
                phase: 'tool_calling',
                activeToolName: lastFunctionCall.name,
              }))
            }
          }

          if (functionResponseNames.length > 0) {
            updateLastMessage((message) => {
              const resolvedNames = functionResponseNames.map((name) =>
                name === 'transfer_to_agent' && message.pendingTransferAgent
                  ? message.pendingTransferAgent
                  : name,
              )

              return {
                ...message,
                phase: 'tool_done',
                activeToolName: null,
                pendingTransferAgent: null,
                completedTools: [
                  ...(message.completedTools || []),
                  ...resolvedNames,
                ],
              }
            })
          }

          if (textChunk) {
            if (event.partial === false) {
              accumulatedText = textChunk
            } else {
              accumulatedText += textChunk
            }

            updateLastMessage((message) => ({
              ...message,
              phase: 'generating',
              content: accumulatedText,
              activeToolName: null,
            }))
          }
        },
        onError: (err) => {
          console.error('Erro no streaming ADK:', err)
          updateLastMessage((message) => ({
            ...message,
            streaming: false,
            phase: null,
            activeToolName: null,
            content:
              message.content ||
              'Desculpe, ocorreu um erro ao processar sua mensagem.',
          }))
        },
        onDone: () => {
          updateLastMessage((message) => ({
            ...message,
            streaming: false,
            phase: null,
            activeToolName: null,
          }))
        },
        onAbort: () => {
          updateLastMessage((message) => ({
            ...message,
            streaming: false,
            phase: null,
            activeToolName: null,
            content: message.content || 'Geração interrompida.',
          }))
        },
        signal,
      },
    )
  }

  // --- Funções públicas do chat ---

  const sendMessage = async (
    textOverride,
    { appName = agentName, context = chatContext } = {},
  ) => {
    const userMessage = (textOverride ?? inputMessage).trim()
    if (!userMessage || isSendingRef.current) return

    if (abortControllerRef.current) abortControllerRef.current.abort()
    abortControllerRef.current = new AbortController()

    setIsSendingSync(true)
    setInputMessage('')
    setMessages((prev) => [...prev, { role: 'human', content: userMessage }])
    setLoading(true)

    try {
      const session = await findOrCreateSession(appName, context)
      await runAdkMessageSSE(
        session,
        userMessage,
        abortControllerRef.current.signal,
        appName,
      )
    } catch (error) {
      console.error('Erro ao enviar mensagem para ADK:', error)
      setMessages((prev) => {
        const messages = [...prev]
        const lastMessage = messages[messages.length - 1]
        if (lastMessage?.streaming) {
          return [
            ...messages.slice(0, -1),
            {
              ...lastMessage,
              streaming: false,
              toolCalls: [],
              content:
                lastMessage.content ||
                'Desculpe, ocorreu um erro ao processar sua mensagem.',
            },
          ]
        }
        return [
          ...messages,
          {
            role: 'ai',
            content: 'Desculpe, ocorreu um erro ao processar sua mensagem.',
            streaming: false,
            toolCalls: [],
          },
        ]
      })
    } finally {
      setIsSendingSync(false)
      setLoading(false)

      const noFocus =
        !document.activeElement || document.activeElement === document.body
      if (noFocus && inputRef.current) inputRef.current.focus()
    }
  }

  const abortMessage = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort()
  }, [])

  // --- Funções de controle do painel ---

  const handleOpenChat = useCallback(() => setChatOpen(true), [])

  const executeNewChat = ({
    agent = DAI_DEFAULT_AGENT,
    context: newContext = {},
    initialMessage: newInitialMessage = null,
  } = {}) => {
    registerMixpanelEvent('new-chat')
    resetChatState()

    setAgentName(agent)
    setChatContext(newContext)
    setInitialMessage(newInitialMessage)
    setChatOpen(true)

    if (newInitialMessage) {
      sendMessage(newInitialMessage, { appName: agent, context: newContext })
    }
  }

  const handleNewChat = (params = {}) => {
    if (chatOpen && hasMessage) {
      pendingNewChatParamsRef.current = params
      setNewChatDialogOpen(true)
      return
    }

    executeNewChat(params)
  }

  const confirmNewChat = () => {
    setNewChatDialogOpen(false)
    executeNewChat(pendingNewChatParamsRef.current ?? {})
    pendingNewChatParamsRef.current = null
  }

  const cancelNewChat = () => {
    setNewChatDialogOpen(false)
    pendingNewChatParamsRef.current = null
  }

  const handleOpenChatPanel = useCallback(() => setChatOpen(true), [])

  const handleCloseChat = useCallback(() => setChatOpen(false), [])

  return (
    <DaiChatContext.Provider
      value={{
        // Controle
        chatOpen,
        agentName,
        chatContext,
        initialMessage,
        handleOpenChat,
        handleNewChat,
        handleOpenChatPanel,
        handleCloseChat,
        newChatDialogOpen,
        confirmNewChat,
        cancelNewChat,
        // Estado da instância
        daiChat,
        messages,
        loading,
        isSending,
        inputMessage,
        hasMessage,
        // Refs
        messagesContainerRef,
        chatContainerRef,
        inputRef,
        // Ações
        sendMessage,
        abortMessage,
        setInputMessage,
        registerMixpanelEvent,
      }}
    >
      {children}
    </DaiChatContext.Provider>
  )
}

export default DaiChatProvider
